<script>
import debounce from 'lodash/debounce'
import {addEvent, mergeEvents} from '@/helpers/Utils'
import Settings from '@/helpers/Settings'

export default {
    name: 'oc-form-field',
    inheritAttrs: false,
    inject: {
        form: {
            default: false
        }
    },
    props: {
        // Use can use this prop instead of (name + v-model)
        // It will do the same but may be slower
        target: {
            type: [String, Number]
        },

        // required
        name: {
            type: [String, Number],
            default() {
                return this.target
            }
        },

        // for v-model to work
        value: {
            default: null
        },

        // attr id
        id: {
            type: [String, Number],
            default: null
        },

        // label
        label: {
            type: [String, Number],
            default: null
        },
        labelFormat: {
            type: [String, Function, Boolean],
            default: 'capitalize'
        },
        labelAttrs: {
            type: Object,
            default: () => ({})
        },

        // help
        help: {
            type: [String, Number],
            default: null
        },
        helpUseIcon: {
            type: Boolean,
            default: false
        },
        helpAttrs: {
            type: Object,
            default: () => ({})
        },

        // input
        inputId: {
            type: [String, Number],
            default: null
        },
        inputIdSuffix: {
            type: [String, Number],
            default: null
        },

        placeholder: {
            type: [String, Number, Boolean],
            default: null
        },
        placeholderPrefix: {
            type: [String, Number],
            default: 'Enter '
        },
        maxlength: {
            type: [String, Number],
            default: null
        },
        input: {
            type: String,
            default: 'oc-text-input'
        },
        inputAttrs: {
            type: Object,
            default: () => ({})
        },
        inputDebounce: {
            type: [Number, String]
        },
        inputDebounceDefault: {
            type: [Number, String],
            default: 250
        },
        feedback: {
            type: Object,
            default: () => ({})
        },
        feedbackUseTooltips: {
            type: Boolean,
            default: false
        },
        // other
        rowAttrs: {
            type: Object,
            default: () => ({})
        },
        inputPrepend: {
            type: [String, Number, Object],
            default: null
        },
        inputAppend: {
            type: [String, Number, Object],
            default: null
        },

        // visibility controls
        displayLabel: {
            type: Boolean,
            default: true
        },
        displayFeedback: {
            type: Boolean,
            default: true
        },
        displayLoader: {
            type: Boolean,
            default: true
        },
        validationDebounce: {
            type: Number,
            default: 300
        },
        validationEvent: {
            type: String,
            default: 'input'
        },
        required: {
            type: Boolean,
            default: null
        },
        size: {
            type: String,
            default: 'normal',
            validator: function (value) {
                return ['small', 'normal', 'big'].indexOf(value) !== -1
            }
        },

        /**
         * These params will trigger red or green input border
         */
        displayIndication: {
            type: Boolean,
            default: true
        },
        indicationInvalid: {
            type: Boolean,
            default: true
        },
        indicationValid: {
            type: Boolean,
            default: true
        },
        /**
         * Auto-register this $_name filed in the Form object.
         * Otherwise validation & submission will not detect the current field if it is not specified in the Form object from the beginning.
         */
        autoRegisterField: {
            type: Boolean,
            default: true
        }
    },
    methods: {
        checkIsValid() {
            return this.debouncedCheckIsValid()
        },
        // states
        isRequired() {
            if( this.required !== null ) {
                return this.required
            }

            return this.form.getValidator().isRequired(this.$_name)
        },
        isVisible() {
            return this.form.getValidator().isVisible(this.$_name)
        },
        isReadonly() {
            return this.processedInputAttrs.readonly || this.form.getValidator().isReadonly(this.$_name)
        },
        isDisabled() {
            return this.processedInputAttrs.disabled || this.form.getValidator().isDisabled(this.$_name)
        },
        getDefaultValue() {
            return this.form.getValidator().getRuleParam('default', this.$_name, 'value')
        },
        _isObject(target) {
            return isObject(target)
        }
    },
    created() {
        if( false === this.form ) {
            throw new Error(`[FormField] Syntax error. The \`<oc-form-field>\` component need to be under <oc-form> component.`)
        }
        else if( this.name === undefined && this.target === undefined ) {
            throw new Error(`[FormField] Please provide property "name".`)
        }

        this.debouncedCheckIsValid = debounce(function() {
            this.form.getValidator().validateField(this.$_name, false)
        }, this.validationDebounce)

        // auto register field
        if( this.autoRegisterField && ! this.form.hasDeep(this.$_name) ) {
            if( Settings.get('debug') ) {
                console.warn(`[FormField] Invalid prop name: "${this.$_name}". Target name is not defined in the Form instance. It will be auto-registered for you.`)
            }
            this.form.setDeep(this.$_name, undefined, false)
        }
    },
    computed: {
        // v-model
        model: {
            get() {
                if( this.target ) {
                    return this.form.getDeep(this.target)
                }
                return this.value
            },
            set(value) {
                // do nothing
                // it will be handled by "input-debounced" event
            }
        },
        $_inputDebounce() {
            let debounce = this.inputDebounce

            if( debounce === undefined ) {
                let allowedDebounce = [
                    'oc-text-input',
                    'oc-textarea-input'
                ]

                let inputType = this.inputAttrs.input || this.input

                debounce = inArray(inputType, allowedDebounce)
                    ? this.inputDebounceDefault
                    : 0
            }

            debounce = parseInt(debounce)

            if( isNaN(debounce) ) {
                console.error(`[FormField::${this.name}]: Invalid debounce parameter.`)
            }

            return debounce
        },
        inputListeners() {
            let listeners = Object.assign({}, this.$listeners)

            let isVModelUsed = this.$vnode.data.model !== undefined

            let originalInputEvent = (value) => {
                if( this.target ) {
                    this.form.setDeep(this.$_name, value, true)
                }
            }

            // extract v-model input event and execute it debounced
            if( isVModelUsed && listeners.input !== undefined ) {
                originalInputEvent = listeners.input
                delete listeners.input
            }

            // normal behavior for all components that do not support "debounce"

            // listen for normal input event
            listeners = addEvent(listeners, 'input', (event) => {
                // w('-original-event | event: input')

                originalInputEvent(event)

                if( this.validationEvent === 'input' ) {
                    this.checkIsValid()
                }
            })

            if( this.validationEvent !== 'input' ) {
                listeners = addEvent(listeners, this.validationEvent, this.checkIsValid)
            }

            // fix for the :$someField.sync
            for(let event in listeners) {
                if( event.indexOf('update:$') !== -1 ) {
                    listeners[event.replace('update:$', 'update:')] = listeners[event]
                    delete listeners[event]
                }
            }

            return listeners
        },

        // for themes extend
        $_inputAppend() {
            return this.inputAppend
        },
        //

        // input helpers
        labelText() {
            let label = this.label // use prop

            if( label === null ) {
                label = this.form.getFieldLabel(this.$_name) // use label object desc
            }

            // format label
            if(this.labelFormat === false) {
                return label
            } else if(typeOf(this.labelFormat) === 'function') {
                return this.labelFormat(label)
            } else {
                return Vue.filter(this.labelFormat)(label)
            }
        },
        $_id() {
            let id = this.id

            if( this.rowAttrs.id !== undefined ) {
                id = this.rowAttrs.id
            }

            return id
        },
        $_name() {
            return String(this.name)
        },
        $_inputId() {
            let id = 'form-field-input-'+ this.nameDashed

            if( this.processedInputAttrs.id !== undefined ) {
                id = this.processedInputAttrs.id
            }

            if( this.inputId !== null ) {
                id = this.inputId
            }

            if( this.inputIdSuffix !== null ) {
                id += '-' + this.inputIdSuffix
            }

            return id
        },
        nameDashed() {
            return this.$_name.replace(/\./g, '-')
        },
        placeholderText() {
            if( this.placeholder === false )
                return

            return this.placeholder ? this.placeholder : this.placeholderPrefix + this.labelText
        },
        $_size() {
            let sizes = {
                small: 'sm',
                normal: 'md',
                big: 'lg'
            }

            return sizes[this.size]
        },

        // help methods
        hasInputPrepend() {
            return this.inputPrepend !== null || this.$slots['input-prepend'] || this.$scopedSlots['input-prepend']
        },
        hasInputAppend() {
            return this.$_inputAppend !== null || this.$slots['input-append'] || this.$scopedSlots['input-append']
        },
        hasErrors() {
            return this.form.getValidator().errors(false).has(
                this.form.getValidator().getUniqueFieldKey(this.$_name)
            )
        },

        // states
        isDirty() {
            return this.form.getValidator().isFieldDirty(this.$_name)
        },
        isLoading() {
            return this.form.getValidator().isFieldLoading(this.$_name)
        },

        /////////

        feedbackAttrs() {
            return Object.assign({
                useTooltips: this.feedbackUseTooltips,
                name: this.$_name
            }, this.feedback)
        },

        processedInputAttrs() {
            let attrs = {}

            for(let param in this.$attrs) {
                if( param.substr(0, 1) === '$' ) {
                    attrs[param.substr(1)] = this.$attrs[param]
                }
            }

            attrs = Object.assign(attrs, this.inputAttrs)

            if( typeof this.inputAttrs.input === 'undefined' ) {
                attrs.input = this.input
            }

            if( typeof this.inputAttrs.debounce === 'undefined' ) {
                attrs.debounce = this.$_inputDebounce
            }

            if( this.maxlength !== null ) {
                attrs.maxlength = this.maxlength
            }
            else if( typeof this._ruleMaxLength !== 'undefined' ) {
                attrs.maxlength = this._ruleMaxLength
            }

            return attrs
        },

        _ruleMaxLength() {
            return this.form.getValidator().getRuleParam('maxlength', this.$_name, 'length')
        },

        inputClasses() {
            let classes = {
                'is-invalid' : this.displayIndication && this.indicationInvalid && this.isDirty && !this.isLoading && this.hasErrors,
                'is-valid'   : this.displayIndication && this.indicationValid && this.isDirty && !this.isLoading && !this.hasErrors
            }

            if( this.processedInputAttrs.class !== undefined ) {
                classes = [classes, this.processedInputAttrs.class]
            }

            return classes
        },

        labelClasses() {
            return this.labelAttrs.class === undefined ? {} : this.labelAttrs.class
        }
    }
}
</script>
